// src/pages/modules/Admin/BackupManager/BackupManager.tsx
// Backup: ดึงทีละ record → สะสมใน memory → zip ดาวน์โหลดทีเดียว
// ต้องติดตั้ง: npm install jszip

import React from 'react'
import JSZip from 'jszip'
import {
  Alert, Badge, Button, Card, Col,
  Form, InputGroup, Modal, ProgressBar, Row, Spinner,
} from 'react-bootstrap'
import { dbRead, dbSet, AuthContext, getShallowKeys, dbRemove } from '../../../../_services/firebase'
import { saveLog } from '../../../../_services/firebase'
import { toastDanger, toastSuccess } from '../../../../_utils'
import ConfirmAlert from '../../../../components/ConfirmAlert/ConfirmAlert'
import {
  DB_DATA_A, DB_DATA_B, DB_DATA_C, DB_DATA_D, DB_DATA_E,
  DB_LOAN, DB_PAYIN, DB_PAYOUT, DB_STATUS,
  DB_REGISTER_FORM, DB_PHOTO,
  DB_LOAN_HISTORY,
  DB_CUST_ROOT,
  DB_CUST_A, DB_CUST_B, DB_CUST_C, DB_CUST_D, DB_CUST_E,
  DB_LOAN_BY_ID, DB_PAYIN_BY_ID, DB_PAYOUT_BY_ID,
  DB_STATUS_BY_ID,
  DB_PHOTO_BY_ID, DB_PHOTO_ITEM,
  DB_FOLLOWING, DB_FOLLOWING_BY_ID, DB_FOLLOWING_CUSTOM_ZONE,
  DB_COLLECT, DB_COLLECT_BY_ID, DB_COLLECT_IMG, DB_COLLECT_IMG_ITEM,
  DB_SLIP_BY_ID, DB_SLIP_BY_ITEM_ID,
  DB_ARCHIVE_VERSION, DB_ARCHIVE_VERSION_BY_ID,
  DB_SCORES, DB_SCORES_BY_ID,
} from '../../../../_config/db_paths'

// ─── Types ────────────────────────────────────────────────────
interface Section {
  key: string
  label: string
  path: string
  itemPath: ((id: string) => string) | null
  desc: string
  color: string
  icon: string
  danger?: boolean
  partSize?: number   // records per json file inside zip
  twoLevel?: boolean                                  // เก็บ 2 ชั้น: cid -> {rid: item} (photo/slip/collectImage)
  childPath?: (cid: string) => string                 // path ระดับลูกค้า (2 ชั้น)
  itemPath2?: (cid: string, rid: string) => string    // path ระดับ leaf (2 ชั้น)
}

const SECTIONS: Section[] = [
  { key: 'data_a', label: 'รอตรวจสอบ (data_a)', path: DB_DATA_A, itemPath: DB_CUST_A, desc: 'ลูกค้ารอตรวจสอบ', color: '#f57c00', icon: 'fa-solid fa-clock' },
  { key: 'data_b', label: 'รอจ่ายเงิน (data_b)', path: DB_DATA_B, itemPath: DB_CUST_B, desc: 'ลูกค้าอนุมัติแล้วรอจ่าย', color: '#1565c0', icon: 'fa-solid fa-hourglass-half' },
  { key: 'data_c', label: 'กำลังผ่อน (data_c)', path: DB_DATA_C, itemPath: DB_CUST_C, desc: 'ลูกค้า active', color: '#2e7d32', icon: 'fa-solid fa-spinner', danger: true },
  { key: 'data_d', label: 'ปิดบัญชี (data_d)', path: DB_DATA_D, itemPath: DB_CUST_D, desc: 'ลูกค้าจบแล้ว', color: '#555', icon: 'fa-solid fa-box-archive' },
  { key: 'data_e', label: 'หนี้เสีย (data_e)', path: DB_DATA_E, itemPath: DB_CUST_E, desc: 'หนี้เสีย', color: '#c62828', icon: 'fa-solid fa-skull-crossbones' },
  { key: 'loan', label: 'ข้อมูลสินเชื่อ', path: DB_LOAN, itemPath: DB_LOAN_BY_ID, desc: 'loan data', color: '#6a1b9a', icon: 'fa-solid fa-receipt', danger: true },
  { key: 'payin', label: 'งวดชำระ', path: DB_PAYIN, itemPath: DB_PAYIN_BY_ID, desc: 'payin installments', color: '#00695c', icon: 'fa-solid fa-money-bill', danger: true },
  { key: 'payout', label: 'ประวัติจ่ายเงิน', path: DB_PAYOUT, itemPath: DB_PAYOUT_BY_ID, desc: 'payout records', color: '#00838f', icon: 'fa-solid fa-hand-holding-dollar' },
  { key: 'status', label: 'สถานะลูกค้า', path: DB_STATUS, itemPath: DB_STATUS_BY_ID, desc: 'status history', color: '#37474f', icon: 'fa-solid fa-flag' },
  /*{ key: 'badDebt', label: 'หนี้เสีย (records)', path: DB_BAD_DEBT, itemPath: DB_BAD_DEBT_BY_ID, desc: 'bad debt records', color: '#b71c1c', icon: 'fa-solid fa-ban' },*/
  { key: 'photo', label: 'รูปภาพลูกค้า', path: DB_PHOTO, itemPath: null, twoLevel: true, childPath: DB_PHOTO_BY_ID, itemPath2: DB_PHOTO_ITEM, desc: 'photos — deep 2 ชั้น', color: '#0277bd', icon: 'fa-solid fa-images', danger: true },
  { key: 'loanH', label: 'ประวัติปรับดอก', path: DB_LOAN_HISTORY, itemPath: null, desc: 'loan history snapshots', color: '#4527a0', icon: 'fa-solid fa-clock-rotate-left' },
  { key: 'form', label: 'Config ฟอร์ม', path: DB_REGISTER_FORM, itemPath: null, desc: 'dynamic form config', color: '#558b2f', icon: 'fa-solid fa-sliders' },
  { key: 'following', label: 'ช่องทางติดต่อ (following)', path: DB_FOLLOWING, itemPath: DB_FOLLOWING_BY_ID, desc: 'contacts + note ต่อลูกค้า', color: '#00897b', icon: 'fa-solid fa-address-book' },
  { key: 'customZone', label: 'โซน override (customZone)', path: DB_FOLLOWING_CUSTOM_ZONE, itemPath: (id: string) => `${DB_FOLLOWING_CUSTOM_ZONE}/${id}`, desc: 'โซนที่ปรับเอง', color: '#5d4037', icon: 'fa-solid fa-location-dot' },
  { key: 'collect', label: 'รับเงินภาคสนาม (collect)', path: DB_COLLECT, itemPath: DB_COLLECT_BY_ID, desc: 'metadata รับเงินภาคสนาม', color: '#ef6c00', icon: 'fa-solid fa-hand-holding-dollar', danger: true },
  { key: 'collectImage', label: 'รูปรับเงินภาคสนาม', path: DB_COLLECT_IMG, itemPath: null, twoLevel: true, childPath: (cid: string) => `${DB_COLLECT_IMG}/${cid}`, itemPath2: DB_COLLECT_IMG_ITEM, desc: 'รูป base64 — deep 2 ชั้น', color: '#f4511e', icon: 'fa-solid fa-camera', danger: true },
  { key: 'slip', label: 'สลิปชำระเงิน', path: DB_SLIP_BY_ID('').replace(/\/$/, ''), itemPath: null, twoLevel: true, childPath: (cid: string) => DB_SLIP_BY_ID(cid), itemPath2: DB_SLIP_BY_ITEM_ID, desc: 'สลิป base64 — deep 2 ชั้น', color: '#0288d1', icon: 'fa-solid fa-receipt', danger: true },
  { key: 'archiveVersion', label: 'เวอร์ชันคลังข้อมูล', path: DB_ARCHIVE_VERSION, itemPath: DB_ARCHIVE_VERSION_BY_ID, desc: 'snapshot แก้ไข Archive', color: '#7b1fa2', icon: 'fa-solid fa-clock-rotate-left', danger: true },
  { key: 'scores', label: 'คะแนนลูกค้า', path: DB_SCORES, itemPath: DB_SCORES_BY_ID, desc: 'scores', color: '#455a64', icon: 'fa-solid fa-star' },
  /*{ key: 'admin', label: 'ข้อมูลพนักงาน', path: DB_ADMIN, itemPath: null, desc: 'admin records', color: '#e65100', icon: 'fa-solid fa-user-shield', danger: true },*/
  /*{ key: 'follow', label: 'โซนพนักงาน', path: DB_FOLLOWER, itemPath: null, desc: 'zone assignments', color: '#4e342e', icon: 'fa-solid fa-map-location-dot' },*/
  /*{ key: 'log', label: 'Action Log', path: DB_ACTION_LOG, itemPath: null, desc: 'ประวัติการใช้งาน', color: '#78909c', icon: 'fa-solid fa-terminal' },*/
]

// ─── Helpers ─────────────────────────────────────────────────
function formatBytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// ─── BackupManager ────────────────────────────────────────────
export default function BackupManager() {
  const { currentUser } = React.useContext(AuthContext)
  const uid = currentUser?.uid || ''

  const [busy, setBusy] = React.useState(false)
  const [progress, setProgress] = React.useState(0)
  const [progressLabel, setProgressLabel] = React.useState('')
  const [logLines, setLogLines] = React.useState<string[]>([])
  const [partSize, setPartSize] = React.useState(100)

  const [confirmReset, setConfirmReset] = React.useState<Section | null>(null)
  const [confirmFull, setConfirmFull] = React.useState(false)
  const [resetInput, setResetInput] = React.useState('')

  const [restoreTarget, setRestoreTarget] = React.useState<Section | null>(null)
  const restoreRef = React.useRef<HTMLInputElement>(null)
  const restoreAllRef = React.useRef<HTMLInputElement>(null)

  const addLog = (msg: string) =>
    setLogLines((p) => [`[${new Date().toLocaleTimeString('th-TH')}] ${msg}`, ...p])

  // ─── Core: fetch records → zip ────────────────────────────
  const backupSection = async (section: Section) => {
    setBusy(true)
    const zip = new JSZip()
    const dateStr = new Date().toISOString().slice(0, 10)
    const size = section.partSize ?? partSize

    try {
      addLog(`📦 เริ่ม backup ${section.label}...`)

      if (section.itemPath) {
        // 1. ดึง keys เท่านั้น ด้วย shallow=true (ไม่ดึง data — ไม่ timeout)
        setProgressLabel(`กำลังดึง keys ของ ${section.label}...`)
        const ids = await getShallowKeys(section.path)
        if (!ids.length) { addLog(`⚠️ ไม่พบข้อมูลใน ${section.label}`); return }
        const chunks = chunkArray(ids, size)
        addLog(`📋 ${ids.length} records → ${chunks.length} ไฟล์ใน zip`)

        // 2. fetch ทีละ record สะสมใน memory แล้วแบ่งเป็นไฟล์ใน zip
        for (let ci = 0; ci < chunks.length; ci++) {
          const chunk: Record<string, any> = {}

          for (let i = 0; i < chunks[ci].length; i++) {
            const id = chunks[ci][i]
            const pct = Math.round(((ci * size + i) / ids.length) * 100)
            setProgress(pct)
            setProgressLabel(`${section.label} — ${ci * size + i + 1}/${ids.length}`)

            const item = await dbRead<any>(section.itemPath!(id))
            if (item) chunk[id] = item
          }

          // บันทึกเป็นไฟล์ในโฟลเดอร์ใน zip
          const pad = String(ci + 1).padStart(2, '0')
          const filename = `${section.key}/${section.key}_part${pad}_of${String(chunks.length).padStart(2, '0')}.json`
          zip.file(filename, JSON.stringify(chunk, null, 2))
          addLog(`  ✓ part ${ci + 1}/${chunks.length} — ${Object.keys(chunk).length} records`)
        }

      } else if (section.twoLevel) {
        // photo: 2-level shallow — customerIds → photoIds → photo item
        const customerIds = await getShallowKeys(section.path)
        if (!customerIds.length) { addLog('⚠️ ไม่พบรูปภาพ'); return }
        addLog(`📋 ${customerIds.length} ลูกค้าที่มีรูป`)

        let photoChunk: Record<string, Record<string, any>> = {}
        let chunkIndex = 0; let totalPhotos = 0

        for (let ci = 0; ci < customerIds.length; ci++) {
          const customerId = customerIds[ci]
          setProgress(Math.round((ci / customerIds.length) * 100))
          setProgressLabel(`Photo ${ci + 1}/${customerIds.length} — ${customerId.slice(0, 20)}...`)

          // shallow keys ของ photos ในลูกค้านี้
          const photoIds = await getShallowKeys(section.childPath!(customerId))
          if (!photoIds.length) continue

          const custPhotos: Record<string, any> = {}
          for (const photoId of photoIds) {
            const photo = await dbRead<any>(section.itemPath2!(customerId, photoId))
            if (photo) { custPhotos[photoId] = photo; totalPhotos++ }
          }
          if (Object.keys(custPhotos).length > 0) photoChunk[customerId] = custPhotos

          // flush ทุก 5 ลูกค้า
          if (Object.keys(photoChunk).length >= 5 || ci === customerIds.length - 1) {
            chunkIndex++
            zip.file(`${section.key}/${section.key}_part${String(chunkIndex).padStart(2, '0')}.json`,
              JSON.stringify(photoChunk, null, 2))
            addLog(`  ✓ photo part${chunkIndex} — ${Object.keys(photoChunk).length} ลูกค้า`)
            photoChunk = {}
          }
        }
        addLog(`  ✅ photo รวม ${totalPhotos} รูป → ${chunkIndex} ไฟล์`)

      } else {
        // flat section — ดึงทีเดียว (ข้อมูลเล็ก)
        setProgressLabel(`กำลังดึง ${section.label}...`)
        const data = await dbRead<any>(section.path)
        if (!data) { addLog(`⚠️ ไม่พบข้อมูลใน ${section.label}`); return }
        zip.file(`${section.key}/${section.key}.json`, JSON.stringify(data, null, 2))
        addLog(`  ✓ ${section.label} — 1 ไฟล์`)
      }

      // เพิ่ม metadata
      zip.file('_meta.json', JSON.stringify({
        section: section.key,
        label: section.label,
        path: section.path,
        backupAt: new Date().toISOString(),
        partSize: size,
      }, null, 2))

      // Generate zip
      setProgressLabel('กำลังบีบอัด zip...')
      setProgress(98)
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } })
      const zipName = `backup_${section.key}_${dateStr}.zip`
      downloadBlob(blob, zipName)

      await saveLog(uid, 'update', section.key, `Backup ${section.label} — ${formatBytes(blob.size)}`)
      addLog(`✅ ดาวน์โหลด ${zipName} — ${formatBytes(blob.size)}`)
      toastSuccess(`Backup ${section.label} เสร็จแล้ว`)

    } catch (e: any) {
      toastDanger(e?.message || 'Backup ไม่สำเร็จ')
      addLog(`✗ ${section.label}: ${e?.message}`)
    } finally {
      setBusy(false)
      setProgress(0)
      setProgressLabel('')
    }
  }

  // ─── Backup ALL ───────────────────────────────────────────
  const backupAll = async () => {
    setBusy(true)
    const zip = new JSZip()
    const dateStr = new Date().toISOString().slice(0, 10)
    const includePhoto = window.confirm('รวมรูปภาพ (photo) ด้วยไหม?\nOK = รวม (zip ใหญ่)\nCancel = ข้าม photo')
    const sections = includePhoto ? SECTIONS : SECTIONS.filter((s) => s.key !== 'photo')

    try {
      addLog(`📦 Full backup — ${sections.length} sections`)
      for (let si = 0; si < sections.length; si++) {
        const section = sections[si]
        const size = section.partSize ?? partSize
        addLog(`📋 ${section.label}...`)

        if (section.itemPath) {
          const ids = await getShallowKeys(section.path)
          if (!ids.length) { addLog(`  ⚠️ ว่าง`); continue }
          const chunks = chunkArray(ids, size)

          for (let ci = 0; ci < chunks.length; ci++) {
            const chunk: Record<string, any> = {}
            for (let i = 0; i < chunks[ci].length; i++) {
              setProgress(Math.round(((si / sections.length) * 100)))
              setProgressLabel(`${section.label} — ${ci * size + i + 1}/${ids.length}`)
              const item = await dbRead<any>(section.itemPath!(chunks[ci][i]))
              if (item) chunk[chunks[ci][i]] = item
            }
            const pad = String(ci + 1).padStart(2, '0')
            zip.file(`${section.key}/${section.key}_part${pad}.json`, JSON.stringify(chunk, null, 2))
          }
          addLog(`  ✓ ${ids.length} records → ${chunks.length} ไฟล์`)
        } else if (section.twoLevel) {
          // photo 2-level
          setProgressLabel('Photo: กำลังดึงรายชื่อลูกค้า...')
          const customerIds = await getShallowKeys(section.path)
          if (!customerIds.length) { addLog('  ⚠️ ไม่พบรูปภาพ'); continue }
          let photoChunk: Record<string, Record<string, any>> = {}
          let chunkIdx = 0; let totalPics = 0

          for (let ci = 0; ci < customerIds.length; ci++) {
            const cid = customerIds[ci]
            setProgressLabel(`Photo ${ci + 1}/${customerIds.length}`)
            const photoIds2 = await getShallowKeys(section.childPath!(cid))
            if (!photoIds2.length) continue
            const custPhotos: Record<string, any> = {}
            for (const pid of photoIds2) {
              const photo = await dbRead<any>(section.itemPath2!(cid, pid))
              if (photo) { custPhotos[pid] = photo; totalPics++ }
            }
            if (Object.keys(custPhotos).length > 0) photoChunk[cid] = custPhotos
            if (Object.keys(photoChunk).length >= 5 || ci === customerIds.length - 1) {
              chunkIdx++
              zip.file(`${section.key}/${section.key}_part${String(chunkIdx).padStart(2, '0')}.json`, JSON.stringify(photoChunk, null, 2))
              photoChunk = {}
            }
          }
          addLog(`  ✓ photo ${totalPics} รูป → ${chunkIdx} ไฟล์`)
        } else {
          setProgressLabel(`${section.label}...`)
          const data = await dbRead<any>(section.path)
          if (data) {
            zip.file(`${section.key}/${section.key}.json`, JSON.stringify(data, null, 2))
            addLog(`  ✓ 1 ไฟล์`)
          } else {
            addLog(`  ⚠️ ว่าง`)
          }
        }
      }

      zip.file('_meta.json', JSON.stringify({ backupAt: new Date().toISOString(), sections: sections.map((s) => s.key) }, null, 2))

      setProgressLabel('กำลังบีบอัด zip...')
      setProgress(99)
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } })
      const zipName = `backup_full_${dateStr}.zip`
      downloadBlob(blob, zipName)

      await saveLog(uid, 'update', 'backup_all', `Full backup — ${formatBytes(blob.size)}`)
      addLog(`🎉 Full backup เสร็จ — ${zipName} (${formatBytes(blob.size)})`)
      toastSuccess(`Full backup เสร็จแล้ว — ${formatBytes(blob.size)}`)

    } catch (e: any) {
      toastDanger(e?.message)
      addLog(`✗ Full backup: ${e?.message}`)
    } finally {
      setBusy(false)
      setProgress(0)
      setProgressLabel('')
    }
  }

  // ─── Restore ─────────────────────────────────────────────
  /*
  const handleRestoreFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !restoreTarget) return
    e.target.value = ''
    setBusy(true)
    setProgressLabel(`Restore ${restoreTarget.label}...`)
    try {
      const text   = new TextDecoder().decode(await file.arrayBuffer())
      const parsed = JSON.parse(text)
      const data   = parsed[restoreTarget.key] ?? parsed

      if (!data || typeof data !== 'object') { toastDanger('รูปแบบไฟล์ไม่ถูกต้อง'); return }

      if (restoreTarget.itemPath) {
        const ids = Object.keys(data)
        for (let i = 0; i < ids.length; i++) {
          setProgress(Math.round((i / ids.length) * 100))
          setProgressLabel(`Restore ${restoreTarget.label} — ${i + 1}/${ids.length}`)
          await dbSet(restoreTarget.itemPath(ids[i]), data[ids[i]])
        }
        addLog(`✅ Restore ${restoreTarget.label} — ${ids.length} records จาก ${file.name}`)
      } else {
        await dbSet(restoreTarget.path, data)
        addLog(`✅ Restore ${restoreTarget.label} จาก ${file.name}`)
      }

      await saveLog(uid, 'update', restoreTarget.key, `Restore ${restoreTarget.label} จาก ${file.name}`)
      toastSuccess(`Restore ${restoreTarget.label} เสร็จ`)
    } catch (e: any) {
      toastDanger(e?.message); addLog(`✗ ${e?.message}`)
    } finally {
      setBusy(false); setProgress(0); setProgressLabel(''); setRestoreTarget(null)
    }
  }
  */
  // ─── Restore (รองรับหลายไฟล์ในโฟลเดอร์เดียว) ──────────────
  const handleRestoreFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList || fileList.length === 0 || !restoreTarget) return
    const files = Array.from(fileList)
      .filter((f) => !f.name.startsWith('_meta'))         // ข้ามไฟล์ metadata
      .sort((a, b) => a.name.localeCompare(b.name))       // part01, part02, ...
    e.target.value = ''
    const target = restoreTarget
    if (files.length === 0) { toastDanger('ไม่พบไฟล์ข้อมูล (เจอแต่ _meta)'); setRestoreTarget(null); return }

    // รวม 1 ระดับ: ถ้า key ซ้ำและทั้งคู่เป็น object ให้ merge (เช่น photo: cid → {pid})
    const mergeInto = (dst: Record<string, any>, src: Record<string, any>) => {
      for (const k of Object.keys(src)) {
        if (dst[k] && typeof dst[k] === 'object' && !Array.isArray(dst[k]) &&
          src[k] && typeof src[k] === 'object' && !Array.isArray(src[k])) {
          Object.assign(dst[k], src[k])
        } else { dst[k] = src[k] }
      }
    }

    setBusy(true); setProgress(0)
    setProgressLabel(`Restore ${target.label} — อ่าน ${files.length} ไฟล์...`)
    try {
      // 1) อ่าน + รวมทุกไฟล์เข้าด้วยกัน
      const merged: Record<string, any> = {}
      for (let fi = 0; fi < files.length; fi++) {
        const f = files[fi]
        setProgressLabel(`อ่านไฟล์ ${fi + 1}/${files.length} — ${f.name}`)
        let parsed: any
        try { parsed = JSON.parse(new TextDecoder().decode(await f.arrayBuffer())) }
        catch { addLog(`✗ ข้าม ${f.name} — JSON ไม่ถูกต้อง`); continue }
        const data = parsed?.[target.key] ?? parsed
        if (!data || typeof data !== 'object') { addLog(`✗ ข้าม ${f.name} — รูปแบบไม่ถูกต้อง`); continue }
        mergeInto(merged, data)
        addLog(`  ✓ อ่าน ${f.name} — ${Object.keys(data).length} รายการ`)
      }

      const ids = Object.keys(merged)
      if (ids.length === 0) { toastDanger('ไม่พบข้อมูลที่ restore ได้'); return }
      addLog(`📋 รวม ${files.length} ไฟล์ → ${ids.length} รายการ`)

      // 2) เขียนลง DB
      if (target.itemPath) {
        for (let i = 0; i < ids.length; i++) {
          setProgress(Math.round((i / ids.length) * 100))
          setProgressLabel(`Restore ${target.label} — ${i + 1}/${ids.length}`)
          await dbSet(target.itemPath(ids[i]), merged[ids[i]])
        }
      } else if (target.twoLevel) {
        for (let i = 0; i < ids.length; i++) {
          setProgress(Math.round((i / ids.length) * 100))
          setProgressLabel(`Restore ${target.label} — ${i + 1}/${ids.length} ลูกค้า`)
          await dbSet(target.childPath!(ids[i]), merged[ids[i]])
        }
      } else {
        setProgressLabel(`Restore ${target.label}...`)
        await dbSet(target.path, merged)
      }
      setProgress(100)

      await saveLog(uid, 'update', target.key, `Restore ${target.label} — ${ids.length} รายการ จาก ${files.length} ไฟล์`)
      addLog(`✅ Restore ${target.label} — ${ids.length} รายการ จาก ${files.length} ไฟล์`)
      toastSuccess(`Restore ${target.label} เสร็จ — ${ids.length} รายการ`)
    } catch (err: any) {
      toastDanger(err?.message); addLog(`✗ ${err?.message}`)
    } finally {
      setBusy(false); setProgress(0); setProgressLabel(''); setRestoreTarget(null)
    }
  }
  // ─── Restore ALL (.zip) ──────────────────────────────────
  // อ่านไฟล์ zip ที่สร้างจาก "Backup ทั้งหมด (.zip)" แล้วเขียนคืนทุก section
  const handleRestoreAllZip = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    // merge 1 ระดับ (key ซ้ำ + เป็น object → รวม เช่น photo: cid → {pid})
    const mergeInto = (dst: Record<string, any>, src: Record<string, any>) => {
      for (const k of Object.keys(src)) {
        if (dst[k] && typeof dst[k] === 'object' && !Array.isArray(dst[k]) &&
          src[k] && typeof src[k] === 'object' && !Array.isArray(src[k])) {
          Object.assign(dst[k], src[k])
        } else { dst[k] = src[k] }
      }
    }

    setBusy(true); setProgress(0)
    setProgressLabel(`เปิดไฟล์ ${file.name}...`)
    try {
      addLog(`📂 เปิด ${file.name} (${formatBytes(file.size)})`)
      const zip = await JSZip.loadAsync(file)

      // อ่าน _meta.json ที่ราก (ถ้ามี)
      const metaFile = zip.file('_meta.json')
      if (metaFile) {
        try {
          const meta = JSON.parse(await metaFile.async('string'))
          if (meta?.backupAt) addLog(`📄 ไฟล์นี้ backup เมื่อ ${new Date(meta.backupAt).toLocaleString('th-TH')}`)
        } catch { /* ignore */ }
      }

      // จัดกลุ่มไฟล์ json ตามโฟลเดอร์ชั้นแรก (= section.key)
      const folders: Record<string, string[]> = {}
      zip.forEach((relPath, entry) => {
        if (entry.dir) return
        if (!relPath.includes('/')) return                 // ไฟล์ราก เช่น _meta.json
        const name = relPath.split('/').pop() || ''
        if (!name.endsWith('.json') || name.startsWith('_meta')) return
        const folder = relPath.split('/')[0]
          ; (folders[folder] ||= []).push(relPath)
      })

      const foundKeys = Object.keys(folders)
      const knownSections = SECTIONS.filter((s) => foundKeys.includes(s.key))
      const unknown = foundKeys.filter((k) => !SECTIONS.some((s) => s.key === k))

      if (knownSections.length === 0) { toastDanger('ไม่พบ section ที่ restore ได้ในไฟล์ zip'); return }
      if (unknown.length) addLog(`⚠️ ข้ามโฟลเดอร์ที่ไม่รู้จัก: ${unknown.join(', ')}`)

      const ok = window.confirm(
        `จะ restore ${knownSections.length} section จาก ${file.name}:\n` +
        knownSections.map((s) => `• ${s.label}`).join('\n') +
        `\n\n⚠️ ข้อมูลเดิมที่ key ซ้ำจะถูกเขียนทับ — ถ้าต้องการคืนค่าแบบสะอาด ให้ Reset ก่อน\nดำเนินการต่อ?`
      )
      if (!ok) { addLog('ยกเลิก restore ทั้งหมด'); return }

      let totalRecords = 0
      for (let si = 0; si < knownSections.length; si++) {
        const section = knownSections[si]
        setProgress(Math.round((si / knownSections.length) * 100))
        setProgressLabel(`Restore ${section.label}...`)
        addLog(`📋 ${section.label}...`)

        // อ่าน + รวมทุก part ในโฟลเดอร์
        const merged: Record<string, any> = {}
        const partPaths = folders[section.key].sort((a, b) => a.localeCompare(b))
        for (const pth of partPaths) {
          let parsed: any
          try { parsed = JSON.parse(await zip.file(pth)!.async('string')) }
          catch { addLog(`  ✗ ข้าม ${pth} — JSON ไม่ถูกต้อง`); continue }
          const data = parsed?.[section.key] ?? parsed
          if (!data || typeof data !== 'object') { addLog(`  ✗ ข้าม ${pth} — รูปแบบไม่ถูกต้อง`); continue }
          mergeInto(merged, data)
        }

        const ids = Object.keys(merged)
        if (ids.length === 0) { addLog(`  ⚠️ ${section.label} ว่าง`); continue }

        // เขียนลง DB
        if (section.twoLevel) {
          // 2 ชั้น: cid → { rid: item } เขียนทีละลูกค้า
          for (let i = 0; i < ids.length; i++) {
            setProgress(Math.round(((si + i / ids.length) / knownSections.length) * 100))
            setProgressLabel(`Restore ${section.label} — ${i + 1}/${ids.length} ลูกค้า`)
            await dbSet(section.childPath!(ids[i]), merged[ids[i]])
          }
        } else if (section.itemPath) {
          for (let i = 0; i < ids.length; i++) {
            setProgress(Math.round(((si + i / ids.length) / knownSections.length) * 100))
            setProgressLabel(`Restore ${section.label} — ${i + 1}/${ids.length}`)
            await dbSet(section.itemPath(ids[i]), merged[ids[i]])
          }
        } else {
          setProgressLabel(`Restore ${section.label}...`)
          await dbSet(section.path, merged)
        }
        totalRecords += ids.length
        addLog(`  ✓ ${section.label} — ${ids.length} รายการ`)
      }

      setProgress(100)
      await saveLog(uid, 'update', 'restore_all', `Restore ทั้งหมด — ${knownSections.length} section, ${totalRecords} รายการ จาก ${file.name}`)
      addLog(`🎉 Restore ทั้งหมดเสร็จ — ${knownSections.length} section, ${totalRecords} รายการ`)
      toastSuccess(`Restore ทั้งหมดเสร็จ — ${totalRecords} รายการ`)
    } catch (err: any) {
      toastDanger(err?.message || 'Restore ทั้งหมดไม่สำเร็จ')
      addLog(`✗ Restore ทั้งหมด: ${err?.message}`)
    } finally {
      setBusy(false); setProgress(0); setProgressLabel('')
    }
  }

  // ─── Reset ───────────────────────────────────────────────
  const handleReset = async () => {
    if (!confirmReset) return
    setBusy(true)
    try {
      await dbSet(confirmReset.path, null)
      await saveLog(uid, 'delete', confirmReset.key, `Reset ${confirmReset.label}`)
      addLog(`🗑️ Reset ${confirmReset.label}`)
      toastSuccess(`Reset ${confirmReset.label} เรียบร้อย`)
    } catch (e: any) {
      toastDanger(e?.message)
    } finally {
      setBusy(false); setConfirmReset(null)
    }
  }

  const handleResetAll = async () => {
    setBusy(true)
    try {
      for (let i = 0; i < SECTIONS.length; i++) {
        setProgress(Math.round((i / SECTIONS.length) * 100))
        setProgressLabel(`Reset ${SECTIONS[i].label}...`)
        await dbSet(SECTIONS[i].path, null)
        addLog(`🗑️ Reset ${SECTIONS[i].label}`)
      }
      await dbSet(DB_CUST_ROOT, null)
      setProgress(100)
      await saveLog(uid, 'delete', 'all', 'Full reset')
      addLog('💥 Full Reset เสร็จแล้ว')
      toastSuccess('Full Reset เรียบร้อย')
    } catch (e: any) {
      toastDanger(e?.message)
    } finally {
      setBusy(false); setProgress(0); setProgressLabel('')
      setConfirmFull(false); setResetInput('')
    }
  }
  const [pathx, setPathx] = React.useState('')
  const removePath = async (path: string) => {
    if (!path.trim()) return
    setBusy(true)
    try {
      await dbRemove(path)
    } finally {
      setBusy(false)
    }
  }

  // ─── Render ───────────────────────────────────────────────
  return (
    <div className='p-3'>
      <ConfirmAlert show={!!confirmReset}
        title={`⚠️ Reset ${confirmReset?.label}`}
        message={`ลบข้อมูลทั้งหมดใน "${confirmReset?.label}"?\nไม่สามารถย้อนกลับได้`}
        confirmLabel='ยืนยัน Reset' variant='danger'
        onConfirm={handleReset} onCancel={() => setConfirmReset(null)} />

      <Modal show={confirmFull} onHide={() => { setConfirmFull(false); setResetInput('') }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15, color: '#c62828' }}>💥 Full Reset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant='danger' style={{ fontSize: 13 }}>
            จะลบ <strong>ข้อมูลทั้งหมด</strong> — ไม่สามารถย้อนกลับได้
          </Alert>
          <Form.Label style={{ fontSize: 12 }}>พิมพ์ <code>RESET</code> เพื่อยืนยัน:</Form.Label>
          <Form.Control size='sm' value={resetInput} onChange={(e) => setResetInput(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={() => { setConfirmFull(false); setResetInput('') }}>ยกเลิก</Button>
          <Button variant='danger' size='sm' disabled={resetInput !== 'RESET'} onClick={handleResetAll}>💥 ยืนยัน</Button>
        </Modal.Footer>
      </Modal>

      <input ref={restoreRef} type='file' accept='.json' multiple style={{ display: 'none' }} onChange={handleRestoreFiles} />
      <input ref={restoreAllRef} type='file' accept='.zip,application/zip' style={{ display: 'none' }} onChange={handleRestoreAllZip} />

      {/* Header */}
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>จัดการฐานข้อมูล</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>Backup (ZIP) / Restore / Reset — Super Admin</div>
        </div>
        <div className='d-flex align-items-center gap-2'>
          <span style={{ fontSize: 12 }}>Records/ไฟล์:</span>
          <Form.Select size='sm' style={{ width: 90 }} value={partSize} onChange={(e) => setPartSize(Number(e.target.value))}>
            {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
          </Form.Select>
        </div>
      </div>

      {/* Progress */}
      {busy && (
        <Card className='mb-3 shadow-sm' style={{ borderColor: '#ffc107' }}>
          <Card.Body style={{ padding: '10px 14px' }}>
            <div className='d-flex align-items-center gap-2 mb-2'>
              <Spinner size='sm' animation='border' variant='warning' />
              <span style={{ fontSize: 13 }}>{progressLabel}</span>
            </div>
            {progress > 0 && <ProgressBar now={progress} label={`${progress}%`} variant='warning' style={{ height: 8 }} animated />}
          </Card.Body>
        </Card>
      )}

      {/* Global actions */}
      <Card className='shadow-sm mb-3'>
        <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>การดำเนินการทั้งระบบ</Card.Header>
        <Card.Body>
          <div className='d-flex gap-2 flex-wrap'>
            <Button variant='primary' size='sm' disabled={busy} onClick={backupAll}>
              <i className='fa-solid fa-file-zipper me-1' />Backup ทั้งหมด (.zip)
            </Button>
            <Button variant='success' size='sm' disabled={busy} onClick={() => restoreAllRef.current?.click()}>
              <i className='fa-solid fa-file-import me-1' />Restore ทั้งหมด (.zip)
            </Button>
            <Button variant='danger' size='sm' disabled={busy} onClick={() => setConfirmFull(true)}>
              <i className='fa-solid fa-trash me-1' />Reset ทั้งหมด
            </Button>
            {
              typeof window !== 'undefined' &&
              (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                <div>
                  <Form.Group>
                    <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>path ที่ต้องการลบ</Form.Label>
                    <Form.Control
                      size='sm'
                      type='text'
                      inputMode='text'
                      placeholder='ใส่ path เช่น /users/123'
                      value={pathx}
                      onChange={(e) => setPathx(e.target.value)}
                    />
                  </Form.Group>

                  <Button
                    variant='danger'
                    size='sm'
                    className='mt-2'
                    disabled={busy || !pathx.trim()}
                    onClick={() => removePath(pathx)}
                  >
                    <i className='fa-solid fa-trash me-1' />ลบ Path
                  </Button>
                </div>
              )
            }
          </div>
          <div className='text-muted mt-2' style={{ fontSize: 11 }}>
            <i className='fa-solid fa-circle-info me-1' />
            ดึงทีละ record สะสมในหน่วยความจำ → บีบอัดเป็น .zip ดาวน์โหลดทีเดียว ไม่ timeout
          </div>
        </Card.Body>
      </Card>

      {/* Section cards */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#555', marginBottom: 10 }}>จัดการแยกส่วน</div>
      <Row className='g-3 mb-3'>
        {SECTIONS.map((s) => (
          <Col key={s.key} xs={6} md={4} lg={3}>
            <Card className='shadow-sm h-100' style={{ borderLeft: `4px solid ${s.color}` }}>
              <Card.Body style={{ padding: '12px 14px' }}>
                <div className='d-flex align-items-center gap-2 mb-1'>
                  <i className={s.icon} style={{ color: s.color, fontSize: 15 }} />
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{s.label}</div>
                  {s.danger && <Badge bg='danger' style={{ fontSize: 9 }}>sensitive</Badge>}
                </div>
                <div className='text-muted mb-3' style={{ fontSize: 11 }}>{s.desc}</div>
                <div className='d-flex flex-column gap-1'>
                  <Button size='sm' variant='outline-primary' disabled={busy} onClick={() => backupSection(s)}>
                    <i className='fa-solid fa-file-zipper me-1' />Backup (.zip)
                  </Button>
                  <Button size='sm' variant='outline-success' disabled={busy}
                    onClick={() => { setRestoreTarget(s); setTimeout(() => restoreRef.current?.click(), 50) }}>
                    <i className='fa-solid fa-upload me-1' />Restore
                  </Button>
                  <Button size='sm' variant='outline-danger' disabled={busy} onClick={() => setConfirmReset(s)}>
                    <i className='fa-solid fa-trash me-1' />Reset
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Log */}
      {logLines.length > 0 && (
        <Card className='shadow-sm'>
          <Card.Header style={{ fontSize: 13, fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
            <span><i className='fa-solid fa-terminal me-2' />Log</span>
            <Button size='sm' variant='outline-secondary' style={{ fontSize: 11 }} onClick={() => setLogLines([])}>ล้าง</Button>
          </Card.Header>
          <Card.Body style={{ padding: 0 }}>
            <div style={{ maxHeight: 250, overflowY: 'auto', background: '#1e1e1e', borderRadius: '0 0 8px 8px', padding: '8px 12px' }}>
              {logLines.map((line, i) => (
                <div key={i} style={{
                  fontFamily: 'monospace', fontSize: 11, lineHeight: 1.7,
                  color: line.includes('✅') || line.includes('🎉') ? '#81c784'
                    : line.includes('✗') || line.includes('ล้มเหลว') ? '#ef9a9a'
                      : line.includes('💥') || line.includes('🗑️') ? '#ff8a65'
                        : line.includes('📦') || line.includes('📋') ? '#ffd54f' : '#bbb',
                }}>{line}</div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}